import { ALERT_KIND_LABEL, ALERT_KINDS, type Alert, type AlertKind, type AlertMonitor, type AlertMonitorInput } from "../../shared/alerts.js";
import type { RuleView } from "../../shared/api.js";
import type { Stack, StackType } from "../../shared/stack.js";
import { Page } from "../Page.js";
import { stackIcon } from "../components/StackIcon.js";
import { StackPicker } from "../components/StackPicker.js";
import { debounce, h, patch } from "../dom.js";
import { full, stackDetail, stackName, time } from "../format.js";

const KIND_HELP: Record<AlertKind, string> = {
  "stock.below": "A stack (or every rule) falls under an amount, or under its rule's min when no amount is given.",
  "craft.failed": "A craft fails, for example missing resources or no pattern.",
  "link.down": "The in-game Butler is disconnected.",
  "power.low": "AE stored power falls under a percentage of the maximum.",
  "game.error": "The Butler reports an error in its log.",
  "production.stopped":
    "A resource stops going up for some minutes while the game is connected: an auto-prod line that jammed, ran out of input or power, or filled its output. " +
    "Without a stack, it watches every resource without a rule that went up in the last day (rules stop rising at target, which is normal).",
  "storage.full":
    "The storage cells have a percentage of their bytes or of their item types used, read through transposers against the ME Drives (see the Storage page).",
};

/** The threshold of a kind: its unit after the value, placeholder, tooltip and form label. Null when it has none. */
const THRESHOLD: Partial<Record<AlertKind, { unit: string; placeholder: string; title: string; label: string }>> = {
  "stock.below": { unit: "", placeholder: "min", title: "Amount; empty means the rule's min", label: "Below (amount; empty means the rule's min)" },
  "power.low": { unit: " %", placeholder: "20", title: "Percent of max AE stored", label: "Below (% of max stored)" },
  "production.stopped": { unit: " min", placeholder: "10", title: "Minutes without going up", label: "Quiet for (minutes)" },
  "storage.full": { unit: " %", placeholder: "90", title: "Percent of the bytes or types used", label: "At (% of bytes or types used)" },
};

const TYPE_LABEL: Record<StackType, string> = { item: "items", fluid: "fluids", essentia: "essentia" };

function perStack(kind: AlertKind): boolean {
  return kind === "stock.below" || kind === "craft.failed" || kind === "production.stopped";
}

/** What "every" covers for a kind: rules, or for production the resources without one. */
function everyLabel(kind: AlertKind, type: StackType | null): string {
  const what = kind === "production.stopped" ? "Every producing resource without a rule" : "Every rule";
  return type ? `${what} (${TYPE_LABEL[type]})` : what;
}

function hasDelay(kind: AlertKind): boolean {
  return kind === "stock.below" || kind === "link.down" || kind === "power.low" || kind === "storage.full";
}

/** What a monitor watches, in a few words. */
function scopeOf(m: AlertMonitor): HTMLElement | string {
  if (!perStack(m.kind)) return "";
  if (m.stack) return h("span", { class: "stack-name" }, stackIcon(m.stack), stackName(m.stack));
  return everyLabel(m.kind, m.stackType);
}

/** Alerts on the dashboard, and the monitors that raise them. */
export class AlertsPage extends Page {
  readonly title = "Alerts";
  private readonly openList = h("div");
  private readonly historyList = h("div");
  private readonly monitorBody = h("tbody");
  private showHistory = false;

  async mount(): Promise<void> {
    const historyToggle = h("input", {
      type: "checkbox",
      onchange: (e: Event) => {
        this.showHistory = (e.target as HTMLInputElement).checked;
        void this.loadHistory();
      },
    });
    this.el.append(
      h(
        "div",
        { class: "card" },
        h(
          "div",
          { class: "row" },
          h("h2", null, "Alerts"),
          h("span", { class: "spacer" }),
          h("button", { class: "small", onclick: () => void this.dismiss("all") }, "Dismiss all"),
        ),
        h("p", { class: "muted small" }, "Alerts stay on every page until clicked. Dismissing one hides it for everyone; it comes back only if its condition clears and happens again."),
        this.openList,
        h("label", { class: "check small" }, historyToggle, "Show dismissed alerts"),
        this.historyList,
      ),
      h(
        "div",
        { class: "card" },
        h("h2", null, "What to watch"),
        h(
          "div",
          { class: "table-wrap" },
          h(
            "table",
            null,
            h("thead", null, h("tr", null, h("th", null, "Alert when"), h("th", null, "Scope"), h("th", null, "Below / quiet for"), h("th", null, "After (min)"), h("th", null, "On"), h("th", null, ""))),
            this.monitorBody,
          ),
        ),
        h("h3", null, "Add a monitor"),
        this.addForm(),
      ),
    );
    this.listen("alerts", () => {
      this.renderOpen();
      if (this.showHistory) void this.loadHistory();
    });
    this.every(30_000, () => this.renderOpen());
    this.renderOpen();
    await this.loadMonitors();
  }

  // ------------------------------------------------------------------- alerts

  private renderOpen(): void {
    const alerts = this.ctx.live.alerts;
    patch(this.openList, alerts.length ? this.alertList(alerts, true) : h("p", { class: "empty" }, "No alerts. All clear."));
  }

  private async dismiss(ids: number[] | "all"): Promise<void> {
    const result = await this.attempt(() => this.ctx.api.dismissAlerts(ids));
    if (result) this.ctx.live.dropAlerts(result.dismissed);
  }

  private async loadHistory(): Promise<void> {
    if (!this.showHistory) {
      patch(this.historyList);
      return;
    }
    const all = await this.attempt(() => this.ctx.api.alerts(true));
    if (!all) return;
    const dismissed = all.filter((a) => a.dismissedAt !== null);
    patch(this.historyList, dismissed.length ? this.alertList(dismissed, false) : h("p", { class: "muted small" }, "Nothing dismissed yet."));
  }

  private alertList(alerts: Alert[], dismissable: boolean): HTMLElement {
    return h(
      "ol",
      { class: "log alert-list" },
      ...alerts.map((a) =>
        h(
          "li",
          { key: a.id, class: `log-${a.level === "error" && a.active ? "error" : a.active ? "warn" : "info"}` },
          h("time", null, time(a.lastAt)),
          h("span", { class: "log-source" }, ALERT_KIND_LABEL[a.kind]),
          h(
            "span",
            { class: "log-msg" },
            h("strong", null, a.title),
            a.count > 1 ? ` (×${a.count}, first ${time(a.firstAt)})` : "",
            a.active ? "" : " · cleared",
            h("br"),
            a.msg,
          ),
          dismissable
            ? h("button", { class: "small", onclick: () => void this.dismiss([a.id]) }, "Dismiss")
            : h("span", { class: "muted small" }, a.dismissedAt ? `dismissed ${time(a.dismissedAt)}` : ""),
        ),
      ),
    );
  }

  // ----------------------------------------------------------------- monitors

  private async loadMonitors(): Promise<void> {
    const monitors = await this.attempt(() => this.ctx.api.monitors());
    if (!monitors) return;
    patch(this.monitorBody, 
      ...(monitors.length
        ? monitors.map((m) => this.monitorRow(m))
        : [h("tr", null, h("td", { colspan: "6", class: "empty" }, "Nothing is watched. Add a monitor below."))]),
    );
  }

  private async save(m: AlertMonitor, change: AlertMonitorInput): Promise<void> {
    const saved = await this.attempt(() => this.ctx.api.updateMonitor(m.id, change), "Monitor saved");
    if (!saved) await this.loadMonitors();
  }

  private monitorRow(m: AlertMonitor): HTMLElement {
    const t = THRESHOLD[m.kind];
    const threshold = t
      ? h("input", {
          type: "number",
          min: "0",
          class: "narrow",
          value: m.threshold === null ? "" : String(m.threshold),
          placeholder: t.placeholder,
          title: t.title,
          onchange: (e: Event) => void this.save(m, { threshold: (e.target as HTMLInputElement).value === "" ? null : Number((e.target as HTMLInputElement).value) }),
        })
      : h("span", { class: "muted" }, "–");
    const delay = hasDelay(m.kind)
      ? h("input", {
          type: "number",
          min: "0",
          step: "any",
          class: "narrow",
          value: String(m.delayMinutes),
          onchange: (e: Event) => void this.save(m, { delayMinutes: Number((e.target as HTMLInputElement).value) || 0 }),
        })
      : h("span", { class: "muted" }, "at once");
    return h(
      "tr",
      { key: m.id, class: m.enabled ? "" : "status-disabled", title: KIND_HELP[m.kind] },
      h("td", null, ALERT_KIND_LABEL[m.kind]),
      h("td", null, scopeOf(m)),
      h("td", null, threshold, t?.unit ?? ""),
      h("td", null, delay),
      h("td", null, h("input", { type: "checkbox", checked: m.enabled, "aria-label": "Enabled", onchange: (e: Event) => void this.save(m, { enabled: (e.target as HTMLInputElement).checked }).then(() => this.loadMonitors()) })),
      h(
        "td",
        { class: "actions" },
        h(
          "button",
          {
            class: "small danger",
            onclick: async () => {
              if (!confirm(`Delete this monitor? Its alerts are dismissed too.`)) return;
              await this.attempt(() => this.ctx.api.deleteMonitor(m.id), "Monitor deleted");
              await this.loadMonitors();
            },
          },
          "Delete",
        ),
      ),
    );
  }

  private addForm(): HTMLElement {
    let chosen: Stack | null = null;
    const kind = h("select", null, ...ALERT_KINDS.map((k) => h("option", { value: k }, ALERT_KIND_LABEL[k])));
    const scope = h("select");
    const scopeOptions = (k: AlertKind) => {
      const value = scope.value;
      scope.replaceChildren(
        h("option", { value: "one" }, "One stack"),
        h("option", { value: "" }, everyLabel(k, null)),
        ...(["item", "fluid", "essentia"] as const).map((t) => h("option", { value: t }, everyLabel(k, t))),
      );
      scope.value = value || "one";
    };
    scopeOptions(kind.value as AlertKind);
    let networkChosen: Stack | null = null;
    const networkPicker = new StackPicker(this.ctx, (stack) => (networkChosen = stack), "network");
    const threshold = h("input", { type: "number", min: "0" });
    const delay = h("input", { type: "number", min: "0", step: "any", value: "0" });
    const help = h("p", { class: "muted small" });
    const search = h("input", { type: "search", placeholder: "Search your rules…", autocomplete: "off" });
    const results = h("ul", { class: "picker-results" });
    const picked = h("div", { class: "picker-chosen muted" }, "No stack chosen");
    const thresholdLabel = h("span");
    const thresholdWrap = h("label", null, thresholdLabel, threshold);
    const delayWrap = h("label", null, "Only after it lasts (minutes)", delay);
    const stackWrap = h("div", { class: "picker" }, search, results, picked);
    const networkWrap = h("div", null, h("p", { class: "muted small" }, "Pick anything the network stores. It is monitored on the Home page if nothing watches it yet, so every change is recorded."), networkPicker.el);
    const scopeWrap = h("label", null, "Scope", scope);

    const pick = (rule: RuleView) => {
      chosen = rule.stack;
      picked.className = "picker-chosen";
      patch(picked, stackIcon(rule.stack), h("strong", null, stackName(rule.stack)), h("span", { class: "muted small" }, `${stackDetail(rule.stack)} · min ${full(rule.min)}`));
      patch(results);
    };
    const runSearch = debounce(async () => {
      const q = search.value.trim();
      if (!q) {
        patch(results);
        return;
      }
      const page = await this.attempt(() => this.ctx.api.rules({ q, limit: 8, sort: "name" }));
      if (!page) return;
      patch(results, 
        ...(page.items.length
          ? page.items.map((r) =>
              h("li", { onclick: () => pick(r) }, stackIcon(r.stack), h("span", null, stackName(r.stack)), h("span", { class: "muted small" }, stackDetail(r.stack)), h("span", { class: "muted small" }, `min ${full(r.min)}`)),
            )
          : [h("li", { class: "empty muted" }, "No rule matches. Only stacks with a rule are watched in game.")]),
      );
    }, 200);
    search.addEventListener("input", runSearch);

    const sync = () => {
      const k = kind.value as AlertKind;
      help.textContent = KIND_HELP[k];
      scopeWrap.hidden = !perStack(k);
      const one = perStack(k) && scope.value === "one";
      stackWrap.hidden = !one || k === "production.stopped";
      networkWrap.hidden = !one || k !== "production.stopped";
      const t = THRESHOLD[k];
      thresholdWrap.hidden = !t;
      thresholdLabel.textContent = t?.label ?? "";
      threshold.placeholder = k === "stock.below" ? "rule's min" : t?.placeholder ?? "";
      delayWrap.hidden = !hasDelay(k);
    };
    kind.addEventListener("change", () => {
      threshold.value = "";
      scopeOptions(kind.value as AlertKind);
      sync();
    });
    scope.addEventListener("change", sync);
    sync();

    return h(
      "form",
      {
        class: "monitor-form",
        onsubmit: async (e: Event) => {
          e.preventDefault();
          const k = kind.value as AlertKind;
          const input: AlertMonitorInput = { kind: k, delayMinutes: Number(delay.value) || 0 };
          if (perStack(k)) {
            if (scope.value === "one") {
              const stack = k === "production.stopped" ? networkChosen : chosen;
              if (!stack) {
                this.ctx.toast("Choose a stack first", "error");
                return;
              }
              input.stack = stack;
            } else input.stackType = (scope.value || null) as StackType | null;
          }
          if (THRESHOLD[k]) input.threshold = threshold.value === "" ? null : Number(threshold.value);
          const created = await this.attempt(() => this.ctx.api.createMonitor(input), "Monitor added");
          if (created) await this.loadMonitors();
        },
      },
      h("div", { class: "row wrap" }, h("label", null, "Alert when", kind), scopeWrap, thresholdWrap, delayWrap),
      help,
      stackWrap,
      networkWrap,
      h("div", { class: "row" }, h("span", { class: "spacer" }), h("button", { class: "primary", type: "submit" }, "Add monitor")),
    );
  }
}
